// The one map from a contract-model date's computed status to what a person
// reads (wireframe 1). The state itself is pangaea's (occurrencestatus.Of on
// the request's as_of day); this file only names it. Every surface that shows
// a deliverable date's state reads its label and tone from here, so a date
// never reads two ways on two pages.
import { onTimeReading } from '../../../../components/delivery/widgets/kpiReadings';
import { cmDate, daysBetween, todayISO } from './format';
/** The occurrencestatus states, in the order a queue sorts them. */ export const OCCURRENCE_STATES = [
    'late',
    'rejected',
    'awaiting_review',
    'due_soon',
    'upcoming',
    'due_unknown',
    'not_covered',
    'waiting_on_event',
    'scheduled',
    'held',
    'approved',
    'waived',
    'covered',
    'not_owed',
    'retired'
];
/** Short labels for counts (KPI tiles, filters): "Late 2". */ export const STATE_SHORT_LABEL = {
    late: 'Late',
    rejected: 'Rejected',
    awaiting_review: 'Awaiting review',
    due_soon: 'Due in 14 days',
    upcoming: 'Upcoming',
    due_unknown: 'Due date unknown',
    not_covered: 'Not covered by evidence',
    waiting_on_event: 'Waiting on an event',
    scheduled: 'Scheduled',
    held: 'Held',
    approved: 'Approved',
    waived: 'Waived by the COR',
    covered: 'Covered by another deliverable',
    not_owed: 'Not owed (never required)',
    retired: 'Retired'
};
/**
 * SAD-866: what not owed is for, said the same way on the menu, the form,
 * the status and its tooltip. It removes a date that should never have
 * existed; it is internal and needs no COR. It is not a waiver, which is the
 * client excusing a date that was owed.
 */ export const NOT_OWED_COPY = {
    menu: 'Mark not owed (date should not exist)',
    title: 'Mark this date not owed',
    help: 'Use this for a date that should never have existed: it was generated in error, or the contract never required it. It is internal: no COR is involved, and a second person here approves it. It is not a waiver. If the client excused a date that was owed, propose a waiver instead.',
    status: 'Not owed (never required)',
    tooltip: 'This date should never have existed: it was generated in error or is outside the contract. Removed internally, with no COR involved. Not a waiver.',
    reasons: {
        generated_in_error: 'Generated in error (a duplicate or a wrong date)',
        not_owed: 'Outside the contract (the contract never required it)'
    }
};
/**
 * SAD-866: where a date decision stands (pangaea derives it). A waiver or an
 * extension is proposed, then the COR agrees, then a second person approves;
 * a not-owed mark skips the COR. SAD-902: submission edit / evidence also skip
 * the COR — use decisionStepLabel, never this map alone, for those kinds.
 */ export const DECISION_STEP_LABEL = {
    proposed: 'Proposed, awaiting the COR',
    awaiting_approval: 'COR agreed, awaiting approval',
    approved: 'Approved',
    cor_declined: 'COR declined',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn'
};
/** Step copy that accounts for kinds with no COR (SAD-902, not_owed). */ export function decisionStepLabel(d) {
    if (d.step === 'awaiting_approval') {
        if (d.kind === 'submission_edit') return 'Submission edit awaiting approval';
        if (d.kind === 'submission_evidence') return 'Added evidence awaiting approval';
        if (d.kind === 'waiver_evidence') return 'Waiver evidence awaiting approval';
        if (d.kind === 'extension_evidence') return 'Extension evidence awaiting approval';
        if (d.kind === 'waiver_edit') return 'Waiver edit awaiting approval';
        if (d.kind === 'not_owed') return 'Not owed: awaiting approval';
    }
    return DECISION_STEP_LABEL[d.step] ?? d.step;
}
/** True when the proposal never involves the COR. */ export function decisionNeedsNoCor(kind) {
    return kind === 'not_owed' || kind === 'submission_edit' || kind === 'submission_evidence' || kind === 'waiver_evidence' || kind === 'extension_evidence' || kind === 'waiver_edit';
}
/** The mark an open decision puts beside its date: "Waiver proposed". */ export function decisionMark(d) {
    if (d.kind === 'submission_edit' || d.kind === 'submission_evidence') {
        return 'Edit pending approval';
    }
    if (d.kind === 'waiver_evidence' || d.kind === 'extension_evidence') return 'Evidence pending approval';
    if (d.kind === 'waiver_edit') return 'Waiver edit pending approval';
    if (d.step === 'proposed') return d.kind === 'extension' ? 'Extension proposed' : 'Waiver proposed';
    if (d.kind === 'not_owed') return 'Not owed: awaiting approval';
    return 'COR agreed: awaiting approval';
}
export const STATE_TONE = {
    late: 'danger',
    rejected: 'danger',
    awaiting_review: 'info',
    due_soon: 'warning',
    upcoming: 'neutral',
    due_unknown: 'neutral',
    not_covered: 'neutral',
    waiting_on_event: 'info',
    scheduled: 'info',
    held: 'success',
    approved: 'success',
    waived: 'neutral',
    covered: 'neutral',
    not_owed: 'neutral',
    retired: 'neutral'
};
/** Review tiers (000213's CHECK), in the order a reviewer picks them. */ export const REVIEW_TIERS = [
    'va_approver',
    'va_pm',
    'va_cor',
    'contractor',
    'unspecified'
];
const TIER_LABEL = {
    va_approver: 'VA Approver',
    va_pm: 'VA PM',
    va_cor: 'VA COR',
    contractor: 'Contractor',
    unspecified: 'Unspecified'
};
/** "VA PM" for va_pm; an unknown tier reads as itself, never blank. */ export function tierLabel(tier) {
    if (!tier) return TIER_LABEL.va_pm;
    return TIER_LABEL[tier] ?? tier;
}
/**
 * The program home Late tile's ?state= value: unfiled late dates plus dates
 * approved after their due date. ?state=late stays unfiled-only (Do Next).
 */ export const LATE_OR_APPROVED_LATE = 'late_or_approved_late';
export function isOccurrenceState(s) {
    return OCCURRENCE_STATES.includes(s);
}
function plural(n, one, many = `${one}s`) {
    return `${n} ${n === 1 ? one : many}`;
}
/** An offset unit, one of it ("day"), for "within 10 days". */ const OFFSET_UNIT_ONE = {
    calendar_day: 'day',
    business_day: 'business day',
    week: 'week',
    month: 'month'
};
/**
 * SAD-967: why a deliverable row holds no firm date, exactly as pangaea
 * reads it from the rules that govern that row (ListDuties
 * undated_reasons). Never inferred here from another row's check.
 */ export function undatedStatus(r) {
    switch(r?.reason){
        case 'on_request':
            {
                const within = r.hasOffset && r.offsetN > 0 ? ` (within ${plural(r.offsetN, OFFSET_UNIT_ONE[r.offsetUnit] ?? 'day')})` : '';
                return r.eventKind === 'on_request' ? {
                    state: 'on_request',
                    tone: 'neutral',
                    label: `On request · when the government asks${within}`
                } : {
                    state: 'on_request',
                    tone: 'neutral',
                    label: `As needed · after each ${r.eventLabel || 'occurrence'}${within}`
                };
            }
        case 'waiting_on_event':
            return {
                state: 'waiting_on_event',
                tone: STATE_TONE.waiting_on_event,
                label: `Projected · waiting on ${r.eventLabel || 'an event'}`
            };
        case 'needs_start_date':
            return {
                state: 'needs_start_date',
                tone: 'warning',
                label: 'Needs a start date'
            };
        default:
            return {
                state: 'none_this_period',
                tone: 'neutral',
                label: 'No dates this period'
            };
    }
}
/** pangaea's check on a deliverable held by an unresolved condition set. */ export const CONDITION_NOT_SETTLED = 'condition_not_settled';
/**
 * SAD-851: a deliverable under one branch of a condition set the award leaves
 * open ("if the solution is …") owes no date until the branch is chosen:
 * "Held: waiting on the PWS 6.1 condition", opening its intake question.
 */ export function conditionHeldStatus(hold) {
    return {
        state: 'condition_held',
        tone: 'warning',
        label: hold ? `Held: waiting on the PWS ${hold.section} condition` : 'Held: waiting on a PWS condition',
        href: hold?.href
    };
}
/**
 * The status line for one date: "Late", "Due in 4 days", "Awaiting VA PM
 * review", "Rejected – resubmit", "Approved, 37 days late", "Waived by the
 * COR", "Not owed (never required)", "Due date unknown", "Scheduled 15 Oct
 * 2024", "Held on 15 Oct 2024, 2 business days late", "Projected · waiting on
 * Technical Kickoff Meeting". A milestone (a meeting, SAD-869) is scheduled,
 * then held: never due, late or reviewed like a deliverable.
 *
 * `finalTier` is the deliverable's final review tier (the review a filing is
 * waiting on). `today` is injectable for tests.
 */ export function occurrenceStatus(occ, finalTier, today = todayISO()) {
    const st = occ.status;
    const state = st?.state ?? '';
    const tone = isOccurrenceState(state) ? STATE_TONE[state] : 'neutral';
    const ev = occ.event;
    switch(state){
        case 'waiting_on_event':
            return {
                state,
                tone,
                label: `Projected · waiting on ${ev?.name || 'an event'}`
            };
        case 'held':
            {
                const on = ev?.heldOn ? ` on ${cmDate(ev.heldOn)}` : '';
                const late = ev?.businessDaysLate ?? 0;
                return late > 0 ? {
                    state,
                    tone: 'warning',
                    label: `Held${on}, ${plural(late, 'business day')} late`
                } : {
                    state,
                    tone,
                    label: `Held${on}`
                };
            }
        case 'scheduled':
            {
                const on = occ.dueOn ? ` ${cmDate(occ.dueOn)}` : '';
                // Past its date and not marked held: someone must say when it was.
                return occ.dueOn && occ.dueOn < today ? {
                    state,
                    tone: 'warning',
                    label: `Scheduled${on} · not marked held`
                } : {
                    state,
                    tone,
                    label: `Scheduled${on}`
                };
            }
        case 'late':
            return {
                state,
                tone,
                label: st?.daysLate ? `Late, ${plural(st.daysLate, 'day')}` : 'Late'
            };
        case 'due_soon':
        case 'upcoming':
            {
                const n = occ.dueOn ? daysBetween(today, occ.dueOn) : null;
                if (n === 0) return {
                    state,
                    tone,
                    label: 'Due today'
                };
                return {
                    state,
                    tone,
                    label: n === null ? 'Upcoming' : `Due in ${plural(n, 'day')}`
                };
            }
        case 'awaiting_review':
            return {
                state,
                tone,
                label: `Awaiting ${tierLabel(finalTier)} review`
            };
        case 'rejected':
            return {
                state,
                tone,
                label: 'Rejected – resubmit'
            };
        case 'approved':
            {
                if (st?.autoApproved) return {
                    state,
                    tone,
                    label: 'Approved (review window lapsed)'
                };
                if (!st?.timingKnown) return {
                    state,
                    tone,
                    label: 'Approved'
                };
                if (st.onTime) return {
                    state,
                    tone,
                    label: 'Approved on time'
                };
                return {
                    state,
                    tone: 'warning',
                    label: `Approved, ${plural(st.daysLate, 'day')} late`
                };
            }
        case 'waived':
            return {
                state,
                tone,
                label: instantWaiver(occ.waiverReason) ? 'Waived instantly' : 'Waived by the COR'
            };
        case 'covered':
            return {
                state,
                tone,
                label: STATE_SHORT_LABEL.covered,
                tooltip: "Another deliverable's own dates deliver this one: it is covered by that series, not waived, and the on-time count reads that series instead."
            };
        case 'not_owed':
            return {
                state,
                tone,
                label: NOT_OWED_COPY.status,
                tooltip: NOT_OWED_COPY.tooltip
            };
        case 'due_unknown':
            return {
                state,
                tone,
                label: 'Due date unknown'
            };
        case 'not_covered':
            return {
                state,
                tone,
                label: STATE_SHORT_LABEL.not_covered,
                tooltip: "Past due with no filing, but the client's record that was imported does not cover this date: it is before that record's first row for this deliverable, or after the record's as-of date. It is not counted as late. Ask the client, or file it if it was delivered."
            };
        case 'retired':
            return {
                state,
                tone,
                label: 'Retired'
            };
        default:
            return {
                state,
                tone: 'neutral',
                label: state || 'Unknown'
            };
    }
}
/**
 * The role an instant approver waived a date as (R21-A: the tenant's admin,
 * a delivery VP over the program or its lead), from the reason's
 * " · instant: <role>" ending; "" for a waiver the COR agreed to.
 */ export function instantWaiver(reason) {
    return /\s·\s*instant:\s*(.+)$/.exec(reason ?? '')?.[1]?.trim() ?? '';
}
/** Open = still owed and not yet approved (a queue row). */ export function isOpenState(state) {
    return [
        'late',
        'rejected',
        'awaiting_review',
        'due_soon',
        'upcoming',
        'due_unknown'
    ].includes(state);
}
/**
 * The on-time share (SAD-998): approved on time, out of approvals with both
 * dates known plus `overdue` dates not filed yet (state_counts.late over the
 * same rows). null when nothing has come due — a KPI must not print 0% for
 * "nothing measured"; overdue with no filing is 0%. See onTimeReading.
 */ export function onTimePercent(counts, overdue) {
    return onTimeReading(counts ? {
        onTime: counts.onTime,
        timingKnown: counts.timingKnown,
        overdue
    } : undefined).pct;
}
export const HANDLING_LABEL = {
    performed: 'Performed',
    routed: 'Routed',
    not_owed: 'Not owed'
};
export const NOT_OWED_REASON_LABEL = {
    waived: 'Waived by the COR',
    not_applicable: 'Does not apply',
    duplicate: 'Duplicate',
    not_listed: 'Not listed in this SOW'
};
export const SOW_STATE = {
    approved_waiting: {
        label: 'Approved, waiting',
        tone: 'neutral'
    },
    in_force: {
        label: 'In force',
        tone: 'success'
    },
    ended: {
        label: 'Ended',
        tone: 'neutral'
    }
};
/**
 * Submitted / to-go counts for one deliverable's dates in the current contract
 * period. Each date counts once. waived, not_owed and retired are excluded.
 * rejected is to go (a resubmission is owed) even though a filing exists.
 * submitted = a filing is on file (latestSubmission) or the state says one was
 * accepted or met: awaiting_review, approved, held (milestone met). Everything
 * else still owed, projected dates included, is to go.
 */ export function submissionProgress(occs) {
    let submitted = 0;
    let toGo = 0;
    for (const o of occs){
        const state = o.status?.state ?? '';
        if ([
            'waived',
            'not_owed',
            'retired'
        ].includes(state)) continue;
        if (state === 'rejected') toGo++;
        else if (o.latestSubmission || [
            'awaiting_review',
            'approved',
            'held'
        ].includes(state)) submitted++;
        else toGo++;
    }
    return {
        submitted,
        toGo
    };
}
const byDue = (a, b)=>(a.dueOn || '9').localeCompare(b.dueOn || '9');
/**
 * The row's next date: the earliest open firm date, else the earliest date
 * projected from an event's planned date (SAD-837: a kickoff's dependents
 * read "7 Oct, projected", never "No dates"), else the latest date.
 */ export function nextOpen(occs) {
    const firm = occs.filter((o)=>!o.event?.projected);
    const open = firm.filter((o)=>isOpenState(o.status?.state ?? '')).sort(byDue);
    const projected = occs.filter((o)=>o.event?.projected).sort(byDue);
    return open[0] ?? projected[0] ?? [
        ...firm
    ].sort((a, b)=>(b.dueOn || '').localeCompare(a.dueOn || ''))[0];
}
/**
 * SAD-1301: a deliverable's next due date: the earliest date still owed (not
 * filed, not approved; projected dates included) in the current period; when
 * that period has none (a changeover day, everything filed), the earliest
 * owed date anywhere on the contract, later periods included. Only with no
 * owed date at all does the current period's own last date stand.
 */ export function nextDue(current, contract) {
    const unfiled = (occs)=>{
        const next = nextOpen(occs.filter((o)=>o.status?.state !== 'awaiting_review'));
        return next && (next.event?.projected || isOpenState(next.status?.state ?? '')) ? next : undefined;
    };
    return unfiled(current) ?? unfiled(contract) ?? nextOpen(current);
}
